import {
  AdminStateRequestDto,
  AdminCalculateRequestDto,
  AdminSubscriberDto,
  AdminFinanceQueryDto,
  FinancialToolkitApiResponse,
} from '../pages/financialToolkit/FinancialToolkit.types';
import type {
  Blog,
  BlogMutationInput,
  CompleteBlogClinicalReviewInput,
  CompleteBlogClinicalReviewResponse,
  GetBlogsParams,
  GetBlogsResponse,
  UpdateBlogResponse,
  Writer,
} from '../pages/blogs/Blog.types';
import { AuthRequest } from '../pages/authRequests/RequestAuth.types';
import type { AuthRequestUpdate } from '../pages/authRequests/authRequestSave';
import { ClientReferral } from '../pages/clientReferrals/ClientReferrals.types';
import { CreateAdminUserDto, UpdateAdminUserDto } from '../pages/manageAdmin/AdminUserList.types';
import {
  ChecklistRow,
  CreateAuditChecklistPayload,
  FieldCatalog,
  UpdateAuditChecklistPayload,
} from '../pages/manageAuditChecklists/types';
import {
  AuditChecklistSummary,
  AuditRunListResponse,
  EditAuditRunPayload,
  TreatmentPlanAuditResult,
} from '../pages/treatmentPlanAuditor/types';
import { RbtCandidate } from '../pages/rbtCandidates/RbtCandidate.types';
import { RbtRequest, RbtRequestStatus } from '../pages/rbtRequests/RequestRbt.types';
import { BillingCacheStats, CacheActionResponse } from '../types/cache.types';
import { SalesforceUserDataResponse, UpdateSalesforceIdsPayload } from './salesforce.types';
import apiClient, { handleUnauthorizedResponse } from './axios';
import { BACKEND_URL } from '../env';

export type AdminClaimsQuery = {
  searchKeyword?: string;
  excluded?: 'true' | 'false';
  candidPrefix?: string;
  renderingProviderName?: string;
  primaryPayerNames?: string;
  serviceDateFrom?: string;
  serviceDateTo?: string;
};

export type AdminClaim = {
  claim_id: string;
  encounter_id?: string | null;
  encounter_external_id?: string | null;
  claim_status?: string | null;
  billable_status?: string | null;
  patient_first_name?: string | null;
  patient_last_name?: string | null;
  patient_control_number?: string | null;
  primary_member_id?: string | null;
  date_of_service?: string | null;
  check_date?: string | null;
  rendering_provider_name?: string | null;
  rendering_provider_npi?: string | null;
  primary_payer_name?: string | null;
  primary_payer_id?: string | null;
  expected_allowed_amount_cents?: number | string | null;
  sum_allowed_amount_cents?: number | string | null;
  excluded: boolean;
};

// API can return claims as a direct array or wrapped in an object with items/data
export type AdminClaimsResponse = AdminClaim[] | { items?: AdminClaim[]; data?: AdminClaim[] };

export type UpdateClaimExcludedResponse = {
  claim_id: string;
  excluded: boolean;
  updatedAt?: string;
};

export type AlohaCandidReconciliationStatus = 'Synced' | 'Not Billable' | 'Waiting' | 'Failed';

export type AlohaCandidReconciliationStatusDetail =
  | 'Incomplete'
  | 'Authorization Exceeded'
  | 'Aloha Validation Error'
  | 'Cancelled'
  | 'Rendering Provider Missing'
  | 'Rendering Provider NPI Missing'
  | 'Insurer Name Not Mapped'
  | 'Waiting to be Bundled'
  | 'Candid Rejected'
  | 'Zero Units'
  | 'Place of Service Missing'
  | 'Bundle Mismatch'
  | 'Provider Not Found'
  | 'Practice Not Found'
  | 'No providers found for office'
  | 'Billing Code Missing'
  | 'Candid Encounter Found'
  | 'Not Billable Appointment Type'
  | 'Pending Candid sync'
  | 'Candid data refresh pending'
  | 'Missing Candid Encounter'
  | 'Missing/Unresolved Candid Prefix'
  | 'Other';

export type AlohaCandidReconciliationQuery = {
  startDate?: string;
  endDate?: string;
  includeExcluded?: boolean;
  renderingProvider?: string;
  clientName?: string;
  practiceName?: string;
  search?: string;
  status?: AlohaCandidReconciliationStatus;
  statusDetail?: AlohaCandidReconciliationStatusDetail;
};

export type AlohaCandidReconciliationSummary = {
  totalAppointments: number;
  completedAppointments: number;
  incompleteAppointments: number;
  cancelledAppointments: number;
  synced: number;
  notBillable: number;
  waiting: number;
  failed: number;
};

export type AlohaCandidReconciliationItem = {
  appointmentId: number;
  clientName: string;
  renderingProvider: string;
  practiceName: string | null;
  candidPrefix: string | null;
  dateOfService: string;
  alohaStatus: 'Completed' | 'Incomplete' | 'Cancelled';
  cancellationType: string | null;
  status: AlohaCandidReconciliationStatus;
  statusDetail: AlohaCandidReconciliationStatusDetail;
  encounterId: string | null;
  encounterExternalId: string | null;
  expectedEncounterExternalId: string | null;
  dateSynced: string | null;
  canResubmit: boolean;
  actions: string[];
  issueId: string | null;
  issueIsExcluded: boolean;
  payerName: string | null;
  billingCode: string | null;
  charge: number | null;
  units: number | null;
  alohaClientId: number | null;
  rawIssueText: string | null;
};

export type AlohaCandidReconciliationResponse = {
  summary: AlohaCandidReconciliationSummary;
  items: AlohaCandidReconciliationItem[];
};

export type AdminMessagesPageParams = {
  limit?: number;
  beforeCreatedAt?: string;
  beforeId?: string;
  afterCreatedAt?: string;
  afterId?: string;
};

export type AdminMessagesPageResponse<T = any> = {
  items: T[];
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
};

const getAdminMessagesConfig = (params?: AdminMessagesPageParams) => ({
  params,
});

export type HaloChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type HaloSource = {
  skill: string;
  system: 'database' | 'salesforce' | 'mock' | 'rag';
  recordIds?: string[];
  salesforceRecords?: {
    objectName: string;
    recordId: string;
    label?: string | null;
  }[];
  portalLinks?: HaloPortalLink[];
  note?: string;
};

export type HaloMessageResponse = {
  message: string;
  sources: HaloSource[];
  salesforceLinks?: HaloSalesforceLink[];
  portalLinks?: HaloPortalLink[];
  threadId: string;
};

export type ProviderChatType =
  'P' | 'P-RBT' | 'P-CLIENT-ACQ' | 'P-INS-OPS' | 'P-TECH' | 'P-CLINICAL';

export type HaloProviderChatDraftPayload = {
  messageId: string;
  chatType: ProviderChatType;
  contextMessageIds?: string[];
};

export type HaloProviderChatDraftRevisionPayload = HaloProviderChatDraftPayload & {
  currentDraft: string;
  revisionInstruction: string;
};

export type HaloProviderChatDraftFeedbackPayload = HaloProviderChatDraftPayload & {
  feedback: 'good' | 'bad';
  note?: string;
  draft?: string;
  threadId?: string;
};

export type HaloMessageFeedbackPayload = {
  threadId: string;
  feedback: 'good' | 'bad';
  note?: string;
  question?: string;
  answer?: string;
};

export type HaloSalesforceLink = {
  objectName: string;
  recordId: string;
  typeLabel: string;
  label: string;
  url: string;
};

export type HaloPortalLink = {
  type: string;
  label: string;
  url: string;
  route: string;
  providerUserId?: string | null;
  clientId?: string | null;
};

export type HaloProviderChatDraftResponse = {
  draft: string;
  confidence: number | null;
  sources: HaloSource[];
  salesforceLinks: HaloSalesforceLink[];
  portalLinks: HaloPortalLink[];
  threadId?: string;
  sourceMessageId: string;
  scopedProviderUserId: string;
};

export type HaloMessageStreamEvent =
  | {
      type: 'status';
      label: string;
    }
  | {
      type: 'final';
      message: string;
      sources: HaloSource[];
      salesforceLinks?: HaloSalesforceLink[];
      portalLinks?: HaloPortalLink[];
      threadId: string;
    }
  | {
      type: 'error';
      code?: string;
      message: string;
      detail?: string;
      failedAnswer?: string;
      threadId?: string;
    };

export type HaloMessageStreamHandlers = {
  onStatus?: (label: string) => void;
};

export class HaloMessageStreamError extends Error {
  detail?: string;
  failedAnswer?: string;
  threadId?: string;

  constructor(event: Extract<HaloMessageStreamEvent, { type: 'error' }>) {
    super(event.detail || event.message);
    this.name = 'HaloMessageStreamError';
    this.detail = event.detail;
    this.failedAnswer = event.failedAnswer;
    this.threadId = event.threadId;
  }
}

export type HaloKnowledgeSourceType = 'file' | 'note' | 'chat_memory_summary' | 'library_document';
export type HaloKnowledgeStatus = 'pending' | 'indexed' | 'failed' | 'deleted';

export type HaloKnowledgeItem = {
  id: string;
  sourceType: HaloKnowledgeSourceType;
  vectorStoreKind: 'knowledge' | 'chat_memory';
  status: HaloKnowledgeStatus;
  title: string;
  textContent?: string | null;
  containsPhi: boolean;
  originalFilename?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  sourceThreadId?: string | null;
  sourceMessageId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  lastIndexedAt?: string | null;
  indexingError?: string | null;
};

export type HaloThreadMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: HaloSource[] | null;
  createdAt: string;
};

export type HaloMemoryThread = {
  id: string;
  title?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  messages?: HaloThreadMessage[];
};

export type HaloKnowledgeListParams = {
  sourceType?: HaloKnowledgeSourceType;
  status?: HaloKnowledgeStatus;
  search?: string;
};

export type HaloKnowledgeNotePayload = {
  title: string;
  content: string;
  containsPhi: boolean;
};

export type HaloKnowledgeFilePayload = {
  title?: string;
  containsPhi: boolean;
  file: File;
};

export type HaloKnowledgeUpdatePayload = {
  title?: string;
  content?: string;
  containsPhi?: boolean;
};

export const api = {
  generateHaloProviderChatDraft: (
    payload: HaloProviderChatDraftPayload
  ): Promise<HaloProviderChatDraftResponse> => {
    return apiClient.post('/admin/ai-chat/provider-chat/drafts', payload).then((res) => res.data);
  },
  reviseHaloProviderChatDraft: (
    payload: HaloProviderChatDraftRevisionPayload
  ): Promise<HaloProviderChatDraftResponse> => {
    return apiClient
      .post('/admin/ai-chat/provider-chat/drafts/revisions', payload)
      .then((res) => res.data);
  },
  submitHaloProviderChatDraftFeedback: (
    payload: HaloProviderChatDraftFeedbackPayload
  ): Promise<{ accepted: true; threadId: string }> => {
    return apiClient
      .post('/admin/ai-chat/provider-chat/drafts/feedback', payload)
      .then((res) => res.data);
  },
  submitHaloMessageFeedback: (
    payload: HaloMessageFeedbackPayload
  ): Promise<{ accepted: true; threadId: string }> => {
    return apiClient.post('/admin/ai-chat/message/feedback', payload).then((res) => res.data);
  },
  streamHaloMessage: async (
    messages: HaloChatMessage[],
    threadId?: string,
    handlers: HaloMessageStreamHandlers = {}
  ): Promise<HaloMessageResponse> => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${BACKEND_URL}/admin/ai-chat/message/stream`, {
      method: 'POST',
      headers: {
        Accept: 'application/x-ndjson',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ messages, ...(threadId ? { threadId } : {}) }),
    });

    if (response.status === 401) {
      let errorMessage: string | undefined;
      try {
        const errorBody = (await response.json()) as { message?: string };
        errorMessage = errorBody.message;
      } catch {
        errorMessage = undefined;
      }
      handleUnauthorizedResponse(errorMessage, response.url, true);
      throw new Error('Halo stream failed because the session expired.');
    }

    if (!response.ok) {
      throw new Error(`Halo stream failed with status ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Halo stream did not return a response body.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResponse: HaloMessageResponse | null = null;

    const handleLine = (line: string) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        return;
      }

      const event = JSON.parse(trimmedLine) as HaloMessageStreamEvent;
      if (event.type === 'status') {
        handlers.onStatus?.(event.label);
        return;
      }

      if (event.type === 'error') {
        throw new HaloMessageStreamError(event);
      }

      finalResponse = {
        message: event.message,
        sources: event.sources,
        salesforceLinks: event.salesforceLinks,
        portalLinks: event.portalLinks,
        threadId: event.threadId,
      };
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      lines.forEach(handleLine);
    }

    buffer += decoder.decode();
    handleLine(buffer);

    if (!finalResponse) {
      throw new Error('Halo stream ended before returning a final response.');
    }

    return finalResponse;
  },
  listHaloKnowledge: (params?: HaloKnowledgeListParams): Promise<HaloKnowledgeItem[]> => {
    return apiClient.get('/admin/ai-chat/knowledge', { params }).then((res) => res.data);
  },
  getHaloMemoryThread: (threadId: string): Promise<HaloMemoryThread> => {
    return apiClient
      .get(`/admin/ai-chat/knowledge/memory/threads/${threadId}`)
      .then((res) => res.data);
  },
  createHaloKnowledgeNote: (payload: HaloKnowledgeNotePayload): Promise<HaloKnowledgeItem> => {
    return apiClient.post('/admin/ai-chat/knowledge/notes', payload).then((res) => res.data);
  },
  uploadHaloKnowledgeFile: (payload: HaloKnowledgeFilePayload): Promise<HaloKnowledgeItem> => {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('containsPhi', String(payload.containsPhi));
    if (payload.title?.trim()) {
      formData.append('title', payload.title.trim());
    }
    return apiClient
      .post('/admin/ai-chat/knowledge/files', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((res) => res.data);
  },
  updateHaloKnowledgeItem: (
    id: string,
    payload: HaloKnowledgeUpdatePayload
  ): Promise<HaloKnowledgeItem> => {
    return apiClient.patch(`/admin/ai-chat/knowledge/${id}`, payload).then((res) => res.data);
  },
  reindexHaloKnowledgeItem: (id: string): Promise<HaloKnowledgeItem> => {
    return apiClient.post(`/admin/ai-chat/knowledge/${id}/reindex`).then((res) => res.data);
  },
  deleteHaloKnowledgeItem: (id: string): Promise<{ id: string; deleted: true }> => {
    return apiClient.delete(`/admin/ai-chat/knowledge/${id}`).then((res) => res.data);
  },
  listTreatmentPlanAuditChecklists: (): Promise<AuditChecklistSummary[]> => {
    return apiClient.get('/treatment-plan-audit/checklists').then((res) => res.data);
  },
  runTreatmentPlanAudit: (file: File, checklistId: string): Promise<TreatmentPlanAuditResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('checklistId', checklistId);
    return apiClient
      .post('/treatment-plan-audit/run', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },
  listAuditChecklists: (includeArchived = false): Promise<ChecklistRow[]> =>
    apiClient
      .get('/audit-checklists', {
        params: { includeArchived: includeArchived ? 'true' : undefined },
      })
      .then((res) => res.data),
  getAuditChecklist: (id: string): Promise<ChecklistRow> =>
    apiClient.get(`/audit-checklists/${id}`).then((res) => res.data),
  createAuditChecklist: (payload: CreateAuditChecklistPayload): Promise<ChecklistRow> =>
    apiClient.post('/audit-checklists', payload).then((res) => res.data),
  updateAuditChecklist: (id: string, payload: UpdateAuditChecklistPayload): Promise<ChecklistRow> =>
    apiClient.patch(`/audit-checklists/${id}`, payload).then((res) => res.data),
  archiveAuditChecklist: (id: string) =>
    apiClient.delete(`/audit-checklists/${id}`).then((res) => res.data),
  restoreAuditChecklist: (id: string) =>
    apiClient.post(`/audit-checklists/${id}/restore`).then((res) => res.data),
  duplicateAuditChecklist: (id: string) =>
    apiClient.post(`/audit-checklists/${id}/duplicate`).then((res) => res.data),
  setProviderPortalAuditChecklist: (id: string): Promise<ChecklistRow> =>
    apiClient.post(`/audit-checklists/${id}/use-for-provider-portal`).then((res) => res.data),
  getAuditChecklistFieldCatalog: (): Promise<FieldCatalog> =>
    apiClient.get('/audit-checklists/field-catalog').then((res) => res.data),
  importAuditChecklistsPreview: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient
      .post('/audit-checklists/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },
  importAuditChecklistsCommit: (payload: Record<string, unknown>) =>
    apiClient.post('/audit-checklists/import/commit', payload).then((res) => res.data),
  listAuditRuns: (
    params: { mine?: boolean; limit?: number; offset?: number } = {}
  ): Promise<AuditRunListResponse> =>
    apiClient
      .get('/treatment-plan-audit/runs', {
        params: {
          mine: params.mine ? 'true' : undefined,
          limit: params.limit,
          offset: params.offset,
        },
      })
      .then((res) => res.data),
  getAuditRun: (id: string): Promise<TreatmentPlanAuditResult> =>
    apiClient.get(`/treatment-plan-audit/runs/${id}`).then((res) => res.data),
  editAuditRun: (id: string, payload: EditAuditRunPayload): Promise<TreatmentPlanAuditResult> =>
    apiClient.patch(`/treatment-plan-audit/runs/${id}`, payload).then((res) => res.data),
  generateAuditMessage: (id: string): Promise<{ message: string }> =>
    apiClient.post(`/treatment-plan-audit/runs/${id}/message`).then((res) => res.data),
  getFeedbackEntries: () => {
    return apiClient.get('/feedback/admin-get-all-feedback').then((res) => {
      return res.data;
    });
  },
  deleteFeedbackEntry: (id: string) => {
    return apiClient.delete(`/feedback/${id}`);
  },
  getContactSubmissions: () => {
    return apiClient.get('/contact/admin-get-all').then((res) => {
      return res.data;
    });
  },
  deleteContactSubmission: (id: string) => {
    return apiClient.delete(`/contact/admin-delete/${id}`);
  },
  getWaitlistEntries: () => {
    return apiClient.get('/messages/admin-get-waitlist').then((res) => res.data);
  },
  deleteWaitlistEntry: (id: string) => {
    return apiClient.delete(`/messages/waitlistEntry/${id}`);
  },
  createAdminUser: (createAdminUserDto: CreateAdminUserDto) => {
    return apiClient.post('/admin', createAdminUserDto);
  },
  updateAdminUser: (userId: string, updateAdminUserDto: UpdateAdminUserDto) => {
    return apiClient.patch(`/admin/${userId}`, updateAdminUserDto);
  },
  deleteAdminUser: (userId: string) => {
    return apiClient.delete(`/admin/${userId}`);
  },
  getAdminUsers: () => {
    return apiClient.get('/admin/all').then((res) => res.data);
  },
  getMyAdminPermissions: () => {
    return apiClient.get('/admin/my');
  },
  login: (data: any) => {
    return apiClient.post('/auth/login', data);
  },
  socialLogin: (data: any) => {
    return apiClient.post('/auth/login/social', data);
  },
  markUserAsActive: (id: string) => {
    return apiClient.post(`/users/${id}/active`);
  },
  fetchUserStatus: (id: string) => {
    return apiClient.get(`/users/${id}/active-status`);
  },
  getPatientsCount: (id: string) => {
    return apiClient.get(`/users/${id}/patients-count`);
  },
  getUnreadMessagesCount: (id: string) => {
    return apiClient.get(`/users/${id}/unread-messages-count`);
  },
  getUnreadConversationsCount: (id: string) => {
    return apiClient.get(`/users/${id}/admin-unread-conversation-counts`);
  },
  updateUserStatus: (id: string, data: any) => {
    return apiClient.patch(`/users/${id}/status`, data);
  },
  getDashboardCharts: (data: any) => {
    let url = `/users/${data.id}/reports?type=${data.type}`;
    if (data.duration) {
      url += `&duration=${data.duration}`;
    }
    if (data.startDate) {
      url += `&startDate=${data.startDate}`;
    }
    if (data.endDate) {
      url += `&endDate=${data.endDate}`;
    }
    return apiClient.get(url).then((r) => r.data);
  },
  getBulletins: () => {
    const url = `/bulletin`;
    return apiClient.get(url).then((res) => res.data);
  },
  getForms: () => {
    const url = `/form`;
    return apiClient.get(url).then((res) => res.data);
  },
  getInsurerMappings: () => {
    const url = `/integrations/aloha-candid/insurer-mappings`;
    return apiClient.get(url);
  },
  getAllPayorsCandid: () => {
    const url = `/integrations/allPayorsCandid`;
    return apiClient.get(url);
  },
  postInsurerMappings: (mappings: { alohaName: string; candidName: string }[]) => {
    const url = `/integrations/aloha-candid/insurer-mappings`;
    return apiClient.post(url, mappings);
  },
  getCandidSyncList: (options?: { excludedOnly?: boolean }) => {
    const url = `/integrations/failed-candid-syncs`;
    const params = options?.excludedOnly ? { excludedOnly: 'true' } : undefined;
    return apiClient.get(url, { params }).then((res) => res.data);
  },
  getAlohaCandidReconciliation: (params?: AlohaCandidReconciliationQuery, signal?: AbortSignal) => {
    return apiClient
      .get<AlohaCandidReconciliationResponse>(`/integrations/aloha-candid/reconciliation`, {
        params,
        signal,
      })
      .then((res) => res.data);
  },
  getClaimsSyncStatus: () => {
    return apiClient.get(`/claims/sync-status`).then((res) => res.data);
  },
  startClaimsSync: () => {
    return apiClient.post(`/claims/sync-candid-encounters`);
  },
  forceClaimsResync: () => {
    return apiClient.post(`/claims/force-resync`);
  },
  getAdminClaims: (params: AdminClaimsQuery) => {
    return apiClient.get<AdminClaimsResponse>(`/admin/claims`, { params }).then((res) => res.data);
  },
  updateClaimExcluded: (claimId: string, excluded: boolean) => {
    return apiClient
      .patch<UpdateClaimExcludedResponse>(`/admin/claims/${claimId}/excluded`, { excluded })
      .then((res) => res.data);
  },
  resubmitCandidSync: (aptId: number) => {
    return apiClient.post(`/integrations/${aptId}/resubmit-failed-candid-sync`);
  },
  resubmitAllCandidSync: () => {
    return apiClient.post(`/integrations/resubmit-all-failed-candid-sync`);
  },
  excludeCandidSyncError: (id: string) => {
    return apiClient.post(`/integrations/candid-sync-errors/${id}/exclude`);
  },
  unexcludeCandidSyncError: (id: string) => {
    return apiClient.post(`/integrations/candid-sync-errors/${id}/unexclude`);
  },
  markFormNotCompleted: (formId: string, userId: string) => {
    return apiClient.post(`/form/${formId}/${userId}/form-not-completed`);
  },
  createForm: (data: {
    title: string;
    description?: string;
    url: string;
    assignedUsers: string[];
  }) => {
    return apiClient.post('/form', data);
  },
  updateForm: (data: {
    id: string;
    title: string;
    description?: string;
    url: string;
    assignedUsers: string[];
  }) => {
    return apiClient.post('/form', data);
  },
  deleteForm: (id: string) => {
    const url = `/form/${id}`;
    return apiClient.delete(url);
  },
  getPartialFamilySignUps: () => {
    return apiClient.get('/users/admin/initial-family-signups').then((res) => res.data);
  },
  getInactivePartialFamilySignUps: () => {
    return apiClient.get('/users/admin/inactive-initial-family-signups').then((res) => res.data);
  },
  getRecaptchaFailedAttempts: (params?: { page?: number; limit?: number }) => {
    const page = params?.page ?? 0;
    const limit = params?.limit ?? 100;
    return apiClient
      .get(`/users/admin/recaptcha-failed-attempts?page=${page}&limit=${limit}`)
      .then((res) => res.data);
  },
  deactivatePartialFamilySignUp: (id: string) => {
    return apiClient.post(`/users/admin/partial-family-signup/deactivate/${id}`);
  },
  reactivatePartialFamilySignUp: (id: string) => {
    return apiClient.post(`/users/admin/partial-family-signup/reactivate/${id}`);
  },
  resendPromptEmailPartialFamilySignUp: (id: string) => {
    return apiClient.post(`/users/admin/partial-family-signup/resend-prompt-email/${id}`);
  },
  getUsers: (data: any, signal?: any) => {
    let url = `/users?page=${data.page}&limit=${data.limit}&showHidden=true`;
    if (data.keyword) {
      url += `&searchKeyword=${data.keyword}`;
    }
    if (data.type) {
      url += `&type=${data.type}`;
    }
    if (data.showAvailable !== undefined) {
      url += `&showAvailable=${data.showAvailable}`;
    }
    if (data.providerTypes && data.providerTypes.length > 0) {
      url += `&providerTypes=${data.providerTypes.join(',')}`;
    }
    if (data.focusAreas && data.focusAreas.length > 0) {
      url += `&focusAreas=${data.focusAreas.join(',')}`;
    }
    if (data.addressState) {
      url += `&addressState=${data.addressState}`;
    }
    if (data.city) {
      url += `&city=${encodeURIComponent(data.city)}`;
    }
    if (data.providerName) {
      url += `&providerName=${encodeURIComponent(data.providerName)}`;
    }
    if (data.providerId) {
      url += `&providerId=${encodeURIComponent(data.providerId)}`;
    }
    if (data.showPending !== undefined) {
      url += `&showPending=${data.showPending}`;
    }
    if (data.showActive !== undefined) {
      url += `&showActive=${data.showActive}`;
    }
    return apiClient.get(url, { signal }).then((res) => res.data);
  },
  getClientProviders: (id: string) => {
    return apiClient
      .get(`/users/${id}/client-providers`)
      .then((res: { data: { providers: [] } }) => res.data);
  },
  getProviders: (data: any) => {
    let url = `/users?type=${data.type}&page=${data.page}&limit=${data.limit}&showHidden=true`;
    if (data.searchKeyword) {
      url += `&searchKeyword=${data.searchKeyword}`;
    }
    if (data.showAvailable) {
      url += `&showAvailable=${data.showAvailable}`;
    }
    if (data.providerTypes && data.providerTypes.length > 0) {
      url += `&providerTypes=${data.providerTypes.join(',')}`;
    }
    if (data.focusAreas && data.focusAreas.length > 0) {
      url += `&focusAreas=${data.focusAreas.join(',')}`;
    }
    if (data.addressState) {
      url += `&addressState=${data.addressState}`;
    }
    return apiClient.get(url).then((res) => res.data);
  },
  getFamilyProviders: (id: string) => {
    return apiClient.get(`/users/${id}/providers/A`);
  },
  getAdminRbtChatProviders: (id: string) => {
    return apiClient.get(`/users/${id}/providers/A-RBT`);
  },
  getAdminClientAcqChatProviders: (id: string) => {
    return apiClient.get(`/users/${id}/providers/A-CLIENT-ACQ`);
  },
  getAdminInsOpsChatProviders: (id: string) => {
    return apiClient.get(`/users/${id}/providers/A-INS-OPS`);
  },
  getAdminTechChatProviders: (id: string) => {
    return apiClient.get(`/users/${id}/providers/A-TECH`);
  },
  getAdminClinicalChatProviders: (id: string) => {
    return apiClient.get(`/users/${id}/providers/A-CLINICAL`);
  },
  getProviderClients: (id: string, params: any) => {
    let url = `/users/${id}/patients?page=${params.page}&type=${params.type}`;
    if (params.limit) {
      url += `&limit=${params.limit}`;
    }
    if (params.keyword) {
      url += `&searchKeyword=${params.keyword}`;
    }
    return apiClient.get(url);
  },
  getClientsForAdminChat: (params: any) => {
    let url = `/users/admin-chat/patients?page=${params.page}&type=${params.type}`;
    if (params.limit) {
      url += `&limit=${params.limit}`;
    }
    if (params.keyword) {
      url += `&searchKeyword=${params.keyword}`;
    }
    return apiClient.get(url);
  },
  updateScheduleStatus: (id: string, data: any) => {
    return apiClient.patch(`/schedules/${id}/status`, data);
  },
  getMessages: (patientId: string, providerId: string) => {
    return apiClient.get(`/messages?patientId=${patientId}&providerId=${providerId}`);
  },
  getAdminClientChatMessages: (patientId: string, params?: AdminMessagesPageParams) => {
    return apiClient.get<AdminMessagesPageResponse>(
      `/messages/client/${patientId}`,
      getAdminMessagesConfig(params)
    );
  },
  getAdminGeneralChatMessages: (providerId: string, params?: AdminMessagesPageParams) => {
    return apiClient.get<AdminMessagesPageResponse>(
      `/messages/A/${providerId}`,
      getAdminMessagesConfig(params)
    );
  },
  getAdminRbtChatMessages: (providerId: string, params?: AdminMessagesPageParams) => {
    return apiClient.get<AdminMessagesPageResponse>(
      `/messages/A-RBT/${providerId}`,
      getAdminMessagesConfig(params)
    );
  },
  getAdminClientAcqChatMessages: (providerId: string, params?: AdminMessagesPageParams) => {
    return apiClient.get<AdminMessagesPageResponse>(
      `/messages/A-CLIENT-ACQ/${providerId}`,
      getAdminMessagesConfig(params)
    );
  },
  getAdminInsOpsMessages: (providerId: string, params?: AdminMessagesPageParams) => {
    return apiClient.get<AdminMessagesPageResponse>(
      `/messages/A-INS-OPS/${providerId}`,
      getAdminMessagesConfig(params)
    );
  },
  getAdminTechMessages: (providerId: string, params?: AdminMessagesPageParams) => {
    return apiClient.get<AdminMessagesPageResponse>(
      `/messages/A-TECH/${providerId}`,
      getAdminMessagesConfig(params)
    );
  },
  getAdminClinicalMessages: (providerId: string, params?: AdminMessagesPageParams) => {
    return apiClient.get<AdminMessagesPageResponse>(
      `/messages/A-CLINICAL/${providerId}`,
      getAdminMessagesConfig(params)
    );
  },
  removeSchedules: (id: string) => {
    return apiClient.delete(`/schedules/${id}`);
  },
  blockUser: (id: string, data: any) => {
    return apiClient.patch(`/users/${id}/status`, data);
  },
  unBlockUser: (id: string, data: any) => {
    return apiClient.patch(`/users/${id}/status`, data);
  },
  confirmUser: (id: string) => {
    return apiClient.patch(`/users/${id}/confirm`);
  },
  removeUser: (id: string) => {
    return apiClient.delete(`/users/${id}`);
  },
  markAsRead: (data: any) => {
    return apiClient.patch('/messages', data);
  },
  // Acknowledgment API for manual checkbox feature (issue #22)
  acknowledgeMessage: (messageId: string, isAcknowledged: boolean) => {
    return apiClient.post(`/messages/${messageId}/acknowledge`, { isAcknowledged });
  },
  // Message editing API
  editMessage: (messageId: string, content: string) => {
    return apiClient.patch(`/messages/${messageId}/edit`, { content });
  },
  // Message assignment API for admin follow-up/action items
  assignMessage: (messageId: string, assignedToUserId: string, chatType: string) => {
    return apiClient.post(`/messages/${messageId}/assign`, { assignedToUserId, chatType });
  },
  unassignMessage: (messageId: string) => {
    return apiClient.delete(`/messages/${messageId}/assign`);
  },
  getAssignedMessages: (page: number = 1, limit: number = 10) => {
    return apiClient.get(`/messages/assigned?page=${page}&limit=${limit}`);
  },
  getAssignedMessagesCount: () => {
    return apiClient.get('/messages/assigned/count');
  },
  sendMessage: (data: any) => {
    return apiClient.post('/messages', data);
  },
  sendMessageAsAdmin: (data: any, adminType: string) => {
    return apiClient.post(`/messages/${adminType}`, data);
  },
  sendMessageAllProvidersAsAdmin: (data: { msgData: any; providerIds: string[] }) => {
    return apiClient.post(`/messages/admin-send-multi`, data);
  },
  promptUserViaSmsAsAdmin: (userId: string) => {
    return apiClient.post(`/messages/prompt-user-via-sms/${userId}`);
  },
  deleteAccount: (id: string) => {
    return apiClient.delete(`/users/${id}`);
  },
  getSchedules: (id: string, field: string) => {
    return apiClient.get(`/schedules?${field}=${id}`);
  },
  scheduleAppointment: (data: any) => {
    return apiClient.post('/schedules', data);
  },
  getScheduleByPatientId: (id: string) => {
    const url = `/schedules?patientId=${id}`;
    return apiClient.get(url).then((res) => res.data);
  },
  getScheduleByProviderId: ({
    provider_id,
    page,
    limit,
  }: {
    provider_id: string;
    page: number;
    limit: number;
  }) => {
    const url = `/schedules?providerId=${provider_id}&page=${page}&limit=${limit}`;
    return apiClient.get(url).then((res) => res.data);
  },
  getDocuments: (userId: string) => {
    const url = `/documents?userId=${userId}`;
    return apiClient.get(url);
  },
  verifyInsurance: (userId: string, insuranceId: string, data: any) => {
    return apiClient.post(`/users/${userId}/insurances/${insuranceId}/verify`, data);
  },
  moveDocument: (documentId: string, data: any) => {
    return apiClient.patch(`/documents/${documentId}`, data);
  },
  renameDocument: (documentId: string, data: any) => {
    return apiClient.patch(`/documents/${documentId}`, data);
  },
  deleteDocument: (documentId: string) => {
    return apiClient.delete(`/documents/${documentId}`);
  },
  createDocument: (data: any) => {
    return apiClient.post('/documents', data);
  },
  getDocumentPublicLink: (documentId: string) => {
    return apiClient.get(`/documents/${documentId}/public-link`).then((res) => res.data);
  },
  connectStripe: (userId: any, tier: string, planType: string) => {
    return apiClient.get(
      `/users/${userId}/stripe-connect-url?tier=${tier}&planType=${planType}&redirectUrl=${window.location.origin}/stripe?code1=success`
    );
  },
  getSubscription: (userId: any) => {
    return apiClient.get(`/users/${userId}/subscriptions`);
  },
  contentFeedback: (userId: any, data: any) => {
    return apiClient.post(`/users/${userId}/content-feedback`, data);
  },
  cancelSubscription: (userId: string, subscriptionId: string) => {
    return apiClient.delete(`/users/${userId}/subscriptions/${subscriptionId}`);
  },
  getInstagramProfile: (userId: any) => {
    return apiClient.get(`/users/${userId}/social-profile`);
  },
  getInstagramPosts: (userId: any) => {
    return apiClient.get(`/users/${userId}/social-posts`);
  },
  initEmailVerification: (id: any) => {
    return apiClient.post(`/users/${id}/email/verify/initiate`);
  },
  verifyEmail: (id: any, data: any) => {
    return apiClient.post(`/users/${id}/email/verify`, data);
  },
  signup: (data: any) => {
    return apiClient.post('/auth/register', data).then((res) => res.data);
  },
  signupFamily: (data: any) => {
    return apiClient.post('/auth/patient/register', data).then((res) => res.data);
  },
  saveInsurance: (userId: string, data: any) => {
    return apiClient.post(`/users/${userId}/insurances`, data);
  },
  validateEmail: (value: string) => {
    return apiClient.post(`/auth/validate-field`, {
      value,
      fieldType: 'email',
    });
  },
  getInsurance: (userId: string) => {
    return apiClient.get(`/users/${userId}/insurances`);
  },
  signupProvider: (data: any) => {
    return apiClient.post('/auth/provider/register', data);
  },
  resendInviteProvider: (userId: string) => {
    return apiClient.post(`/auth/resend-invite/`, { userId });
  },
  createBulletin: (data: any) => {
    return apiClient.post('/bulletin/', data);
  },
  updateBulletin: (data: any) => {
    return apiClient.patch(`/bulletin/${data.id}`, data);
  },
  deleteBulletin: (id: string) => {
    return apiClient.delete(`/bulletin/${id}`);
  },
  getConfig: () => {
    return apiClient.get('/configs').then((res) => res.data);
  },
  saveConfig: (data: any) => {
    return apiClient.post('/configs', data);
  },
  logout: () => {
    return apiClient.post('/auth/logout').then((res) => res.data);
  },
  forgotPassword: (data: any) => {
    return apiClient.post('/auth/forgot-password', data);
  },
  resetPassword: (data: any) => {
    return apiClient.post('/auth/reset-password', data);
  },
  changePassword: (userId: string, data: any) => {
    return apiClient.patch(`/users/${userId}/password`, data);
  },
  getOnboardingSections: () => {
    return apiClient.get('/onboarding/sections').then((res) => res.data);
  },
  getOnboardingSection: (code: string) => {
    return apiClient.get(`/onboarding/sections/${code}`).then((res) => res.data);
  },
  saveOnboardingSection: (code: string, data: any) => {
    return apiClient.put(`/onboarding/sections/${code}`, data);
  },
  getUserProfile: (id: string) => {
    return apiClient.get(`/users/${id}`).then((res) => res.data);
  },
  saveLastActive: (id: string) => {
    return apiClient.post(`/users/${id}/active`);
  },
  saveUserProfile: (id: string, data: any) => {
    return apiClient.put(`/users/${id}`, data);
  },
  uploadLegalDocument: (id: string, data: any) => {
    return apiClient.post(`/users/${id}/legal-documents`, data);
  },
  generatePrompt: (id: string, data: any, signal?: any) => {
    return apiClient.post(`/users/${id}/prompts`, data, { signal });
  },
  getProgressDashboard: (userId: string, month: string, year: string) => {
    return apiClient
      .get(`/users/${userId}/progress-dashboard?month=${month}&year=${year}`)
      .then((r) => r.data);
  },
  getDashboard: (userId: string) => {
    return apiClient.get(`/users/${userId}/dashboard`).then((r) => r.data);
  },
  getBillingDashboard: (
    userId: string,
    params: {
      reportType: string;
      [key: string]: string | number | boolean | undefined;
    }
  ) => {
    return apiClient.get(`/claims/${userId}/billing-dashboard`, { params }).then((r) => r.data);
  },
  getBillingSessions: (
    userId: string,
    params?: {
      [key: string]: string | number | boolean | undefined;
    }
  ) => {
    return apiClient.get(`/claims/${userId}/billing-sessions`, { params }).then((r) => r.data);
  },
  getUserReports: (userId: string) => {
    return apiClient.get(`/users/${userId}/reports`).then((r) => r.data);
  },
  getPredesignedUrl: ({
    id,
    mimeType,
    uploadType,
    fileName,
  }: {
    id: string;
    mimeType: string;
    uploadType: string;
    fileName?: string;
  }) => {
    let url = `/users/${id}/images/signed-url?mimeType=${mimeType}&uploadType=${uploadType}`;
    if (fileName) {
      url += `&fileName=${fileName}`;
    }
    return apiClient.get(url).then((r) => r.data);
  },
  getClientReferrals: async (providerReferralsOnly?: boolean) => {
    const params =
      providerReferralsOnly === true
        ? '?providerReferralsOnly=true'
        : providerReferralsOnly === false
          ? '?providerReferralsOnly=false'
          : '';
    return (await apiClient.get(`/users/client-referrals/all-client-referrals${params}`)).data;
  },
  updateClientReferral: async (clientReferral: ClientReferral) => {
    return await apiClient.patch(
      `/users/client-referrals/${clientReferral.id}/update-referral`,
      clientReferral
    );
  },
  updateClientReferralSalesforceStatus: async (referralId: string, addedToSalesforce: boolean) => {
    return await apiClient.patch(`/users/client-referrals/${referralId}/addedToSalesforce`, {
      addedToSalesforce,
    });
  },
  getAuthRequests: async () => {
    return (await apiClient.get('/authrequest')).data;
  },
  getAuthRequestsAsAdmin: async () => {
    return (await apiClient.get('/authrequest/admin/all-requests')).data;
  },
  getAllPatients: async () => {
    return (await apiClient.get('/authrequest/all-patients')).data;
  },
  createAuthRequest: async (createRequest: AuthRequest) => {
    return await apiClient.post('/authrequest', createRequest);
  },
  updateAuthRequest: async (updateRequest: AuthRequestUpdate) => {
    return await apiClient.patch(`/authrequest/${updateRequest.id}`, updateRequest);
  },
  deleteAuthRequest: async (id: string) => {
    return await apiClient.delete(`/authrequest/${id}`);
  },
  getAuthRequestPointPersons: async () => {
    return (await apiClient.get('/authrequest/admin/point-persons')).data;
  },
  adminGetAllRbtRequests: async () => {
    let requests = (await apiClient.get('/rbtrequest/admin/all-requests')).data;
    requests = requests?.map((r) => ({
      ...r,
      shiftsNeeded: r.shiftsNeeded.map((shift) => {
        if (!isNaN(Date.parse(shift.startTime))) return shift;

        const newStart = new Date();
        newStart.setHours(shift.startTime.split(':')[0]);
        newStart.setMinutes(shift.startTime.split(':')[1]);
        const newEnd = new Date();
        newEnd.setHours(shift.endTime.split(':')[0]);
        newEnd.setMinutes(shift.endTime.split(':')[1]);
        return {
          ...shift,
          startTime: newStart,
          endTime: newEnd,
        };
      }),
    }));
    return requests ?? [];
  },
  createRbtRequest: async (createRequest: RbtRequest) => {
    const data = createRequest as any;
    data.shiftsNeeded = convertShiftsTimeToCustomFormat(createRequest.shiftsNeeded);
    return await apiClient.post('/rbtrequest', data);
  },
  updateRbtRequest: async (id, updateRequest: RbtRequest) => {
    const data = updateRequest as any;
    if (updateRequest.status === RbtRequestStatus.IN_PROCESS) {
      data.status = 'In Progress';
    }
    data.shiftsNeeded = convertShiftsTimeToCustomFormat(updateRequest.shiftsNeeded);
    return await apiClient.patch(`/rbtrequest/${id}`, data);
  },
  deleteRbtRequest: async (id: string, cancellationReason: string) => {
    return await apiClient.delete(`/rbtrequest/${id}`, { data: { cancellationReason } });
  },
  getRbtCandidates: async () => {
    let candidates = (await apiClient.get('/rbtcandidate')).data;
    candidates = candidates?.map((c) => ({
      ...c,
      scheduleAvailability: c.scheduleAvailability.map((shift) => {
        if (!isNaN(Date.parse(shift.startTime))) return shift;

        const newStart = new Date();
        newStart.setHours(shift.startTime.split(':')[0]);
        newStart.setMinutes(shift.startTime.split(':')[1]);
        const newEnd = new Date();
        newEnd.setHours(shift.endTime.split(':')[0]);
        newEnd.setMinutes(shift.endTime.split(':')[1]);
        return {
          ...shift,
          startTime: newStart,
          endTime: newEnd,
        };
      }),
    }));
    return candidates ?? [];
  },
  createRbtCandidate: async (createRequest: RbtCandidate) => {
    const data = createRequest as any;
    data.scheduleAvailability = convertShiftsTimeToCustomFormat(createRequest.scheduleAvailability);
    return await apiClient.post('/rbtcandidate', data);
  },
  updateRbtCandidate: async (id, updateRequest: RbtCandidate) => {
    const data = updateRequest as any;
    data.scheduleAvailability = convertShiftsTimeToCustomFormat(updateRequest.scheduleAvailability);
    return await apiClient.patch(`/rbtcandidate/${id}`, data);
  },
  deleteRbtCandidate: async (id: string) => {
    return await apiClient.delete(`/rbtcandidate/${id}`);
  },
  adminLoginAsClient: async (id: string) => {
    return await apiClient.get(`/users/admin-login/${id}/as-client/`).then((res) => res.data);
  },
  adminLoginAsProvider: async (id: string) => {
    return await apiClient.get(`/users/admin-login/${id}/as-provider/`).then((res) => res.data);
  },
  adminAssignProvider: async (patientId: string, providerId: string) => {
    return await apiClient.post(`/users/admin/patients/${patientId}/assign-provider`, {
      providerId,
    });
  },
  adminUnassignProvider: async (patientId: string, providerId: string) => {
    return await apiClient.post(
      `/users/admin/patients/${patientId}/unassign-provider/${providerId}`
    );
  },
  getSalesforceUserData: async (userId: string) => {
    return apiClient
      .get<SalesforceUserDataResponse>(`/api/salesforce/users/${userId}/salesforce-data`)
      .then((res) => res.data);
  },
  getSalesforceLeadById: async (leadId: string) => {
    return apiClient
      .get<Record<string, unknown>>(`/api/salesforce/leads/${leadId}`)
      .then((res) => res.data);
  },
  getClientAvailability: async (
    opportunityId: string
  ): Promise<{
    availability: Array<{ dayOfWeek: string; startTime: string; endTime: string }>;
  }> => {
    return apiClient
      .get(`/api/salesforce/opportunities/${opportunityId}/client-availability`)
      .then((res) => res.data);
  },
  updateClientAvailability: async (
    opportunityId: string,
    availability: Array<{ dayOfWeek: string; startTime: string; endTime: string }>
  ): Promise<{ deleted: number; created: number }> => {
    return apiClient
      .put(`/api/salesforce/opportunities/${opportunityId}/client-availability`, { availability })
      .then((res) => res.data);
  },
  updateSalesforceUserIds: async (userId: string, payload: UpdateSalesforceIdsPayload) => {
    return apiClient
      .patch<SalesforceUserDataResponse>(`/api/salesforce/users/${userId}/salesforce-ids`, payload)
      .then((res) => res.data);
  },
  updateAddedToSalesforceStatus: async (patientId: string, addedToSalesforce: boolean) => {
    return await apiClient.patch(`/users/admin/patients/${patientId}/addedToSalesforce`, {
      addedToSalesforce,
    });
  },
  updatePatientInternalNotes: async (patientId: string, internalNotes: string) => {
    return await apiClient.patch(`/users/admin/patients/${patientId}/internal-notes`, {
      internalNotes,
    });
  },
  updatePatientProviderNotes: async (patientId: string, providerNotes: string) => {
    return await apiClient.patch(`/users/admin/patients/${patientId}/provider-notes`, {
      providerNotes,
    });
  },
  getBlogs: (params: GetBlogsParams, signal?: AbortSignal): Promise<GetBlogsResponse> => {
    return apiClient.get('/blogs/admin', { params, signal }).then((res) => res.data);
  },
  createBlog: (data: BlogMutationInput): Promise<Blog> =>
    apiClient.post('/blogs', data).then((res) => res.data),
  updateBlog: (id: string, data: BlogMutationInput): Promise<UpdateBlogResponse> =>
    apiClient.patch(`/blogs/${id}`, data).then((res) => res.data),
  completeBlogClinicalReview: (
    id: string,
    data: CompleteBlogClinicalReviewInput
  ): Promise<CompleteBlogClinicalReviewResponse> =>
    apiClient.patch(`/blogs/${id}/clinical-review`, data).then((res) => res.data),
  deleteBlog: (id: string) => apiClient.delete(`/blogs/${id}`).then((res) => res.data),
  getTopics: async (params: any) => {
    const response = await apiClient.get('/blogs/topics', { params });
    return response.data;
  },
  createTopic: async (topic: any) => {
    const response = await apiClient.post('/blogs/topics', topic);
    return response.data;
  },
  updateTopic: async (id: string, topic: any) => {
    const response = await apiClient.patch(`/blogs/topics/${id}`, topic);
    return response.data;
  },
  deleteTopic: async (id: string) => {
    const response = await apiClient.delete(`/blogs/topics/${id}`);
    return response.data;
  },
  getWriters: async (params: { page?: number; limit?: number; keyword?: string }) => {
    const response = await apiClient.get('/blogs/writers', { params });
    return response.data;
  },
  createWriter: (writer: Writer) => {
    return apiClient.post('/blogs/writers', writer);
  },
  updateWriter: (id: string, writer: Writer) => {
    return apiClient.patch(`/blogs/writers/${id}`, writer);
  },
  deleteWriter: (id: string) => {
    return apiClient.delete(`/blogs/writers/${id}`);
  },
  getFinancialToolkitStateRequests: (
    query?: AdminFinanceQueryDto
  ): Promise<FinancialToolkitApiResponse<AdminStateRequestDto>> => {
    return apiClient
      .get('/admin/finance/state-requests', { params: query })
      .then((res) => res.data);
  },

  getFinancialToolkitCalculateRequests: (
    query?: AdminFinanceQueryDto
  ): Promise<FinancialToolkitApiResponse<AdminCalculateRequestDto>> => {
    return apiClient
      .get('/admin/finance/calculate-requests', { params: query })
      .then((res) => res.data);
  },

  getFinancialToolkitSubscribers: (
    query?: AdminFinanceQueryDto
  ): Promise<FinancialToolkitApiResponse<AdminSubscriberDto>> => {
    return apiClient.get('/admin/finance/subscribers', { params: query }).then((res) => res.data);
  },

  getFinancialToolkitStateRequestById: async (id: string): Promise<AdminStateRequestDto> => {
    return (await apiClient.get(`/admin/finance/state-requests/${id}`)).data;
  },

  getFinancialToolkitCalculateRequestById: async (
    id: string
  ): Promise<AdminCalculateRequestDto> => {
    return (await apiClient.get(`/admin/finance/calculate-requests/${id}`)).data;
  },

  getFinancialToolkitSubscriberById: async (id: string): Promise<AdminSubscriberDto> => {
    return (await apiClient.get(`/admin/finance/subscribers/${id}`)).data;
  },

  // Cache Management APIs
  clearAllCache: async (): Promise<CacheActionResponse> => {
    return (await apiClient.post('/users/cache/clear-all')).data;
  },

  clearUserBillingCache: async (userId: string): Promise<CacheActionResponse> => {
    return (await apiClient.post(`/users/cache/clear-user/${userId}`)).data;
  },

  getBillingCacheStats: async (): Promise<BillingCacheStats> => {
    return (await apiClient.get('/users/cache/stats')).data;
  },
  clearCachePattern: async (pattern: string): Promise<CacheActionResponse> => {
    return (await apiClient.post('/users/cache/clear-pattern', { pattern })).data;
  },
  performCacheAction: async ({
    method,
    path,
    body,
  }: {
    method: string;
    path: string;
    body?: Record<string, unknown>;
  }): Promise<CacheActionResponse> => {
    const upperMethod = method.toUpperCase();
    const requestConfig: any = {
      method: upperMethod,
      url: path,
    };

    if (upperMethod === 'GET') {
      if (body) {
        requestConfig.params = body;
      }
    } else if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(upperMethod)) {
      if (body && Object.keys(body).length > 0) {
        requestConfig.data = body;
      }
    }

    return (await apiClient.request(requestConfig)).data;
  },

  // Admin notification management
  adminListNotifications: (params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    providerId?: string;
    category?: string;
    completed?: 'all' | 'completed' | 'not_completed';
    createdAtFrom?: string;
    createdAtTo?: string;
  }) => {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
    return apiClient.get(`/admin/notifications?${query.toString()}`).then((r) => r.data);
  },
  adminBulkDeleteNotifications: (body: {
    ids?: string[];
    applyFilters?: boolean;
    providerId?: string;
    category?: string;
    completed?: 'all' | 'completed' | 'not_completed';
    createdAtFrom?: string;
    createdAtTo?: string;
  }) => {
    return apiClient.post(`/admin/notifications/bulk-delete`, body).then((r) => r.data);
  },
};

const convertShiftsTimeToCustomFormat = (shifts) =>
  shifts.map((shift) => {
    const start = new Date(shift.startTime);
    const startMins = start.getMinutes() < 10 ? `${start.getMinutes()}0` : start.getMinutes();
    const end = new Date(shift.endTime);
    const endMins = end.getMinutes() < 10 ? `${end.getMinutes()}0` : end.getMinutes();
    return {
      ...shift,
      startTime: `${start.getHours()}:${startMins}`,
      endTime: `${end.getHours()}:${endMins}`,
    };
  });
